import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import InfoIcon from "../../../assets/icons/info-icon.svg";
import FinanceCardChart from "../financeCardChart/financeCardChart";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const FinanceInvoicing = ({ agreements = [], dateRange = null, code }) => {
  const [showPopover, setShowPopover] = useState(false);
  const [showDataModal, setShowDataModal] = useState(false);
  const [clickedMonthData, setClickedMonthData] = useState(null);
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const navigate = useNavigate();

  const data = {
    labels: agreements.map((agreement) => agreement.month),
    datasets: [
      {
        label: "Acordos",
        data: agreements.map((agreement) => agreement.value),
        borderColor: "#0962AD",
        backgroundColor: "#0962AD",
        borderWidth: 2,
        yAxisID: "y",
        // tension: 0.3
      },
      {
        label: "Valor Recebido (R$)",
        data: agreements.map((agreement) => agreement.totalReceivedValue),
        borderColor: "#2BAD70",
        backgroundColor: "#2BAD70",
        borderWidth: 2,
        yAxisID: "y1",
        // tension: 0.3
      }
    ]
  };


  const formatMonthDisplay = (monthStr) => {
    if (!monthStr) return '';
    
    if (monthStr.includes('/')) {
      return monthStr;
    }
    
    const parts = monthStr.split('-');
    if (parts.length === 2) {
      const [year, month] = parts;
      const monthNames = [
        'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
      ];
      const monthIndex = parseInt(month) - 1;
      if (monthIndex >= 0 && monthIndex < 12) {
        return `${monthNames[monthIndex]}/${year}`;
      }
    }
    
    return monthStr;
  };

  const handleChartClick = (event, elements) => {
    if (elements && elements.length > 0) {
      const clickedElement = elements[0];
      const pointIndex = clickedElement.index;
      const monthRaw = agreements[pointIndex].month;
      
      console.log('Mês de acordo clicado:', monthRaw);
      
      setClickedMonthData({
        raw: monthRaw,
        formatted: formatMonthDisplay(monthRaw)
      });
      setModalPosition({
        x: event.native.clientX,
        y: event.native.clientY
      });
      setShowDataModal(true);
    }
  };

  const handleNavigateToData = () => {
    if (!clickedMonthData) {
      setShowDataModal(false);
      return;
    }
    
    const monthRaw = clickedMonthData.raw;
    let year, month;
    
    const monthMap = {
      'jan': '01', 'fev': '02', 'mar': '03', 'abr': '04',
      'mai': '05', 'jun': '06', 'jul': '07', 'ago': '08',
      'set': '09', 'out': '10', 'nov': '11', 'dez': '12'
    };
    
    if (monthRaw.includes('-') && monthRaw.split('-').length === 2) {
      const parts = monthRaw.split('-');
      const firstPart = parts[0];
      const secondPart = parts[1];
      
      if (firstPart.length === 4) {
        year = firstPart;
        month = secondPart.padStart(2, '0');
      } else {
        month = firstPart.padStart(2, '0');
        year = secondPart;
      }
    } else if (monthRaw.includes('/')) {
      const parts = monthRaw.split('/');
      if (parts.length === 2) {
        const firstPart = parts[0].toLowerCase().trim();
        const secondPart = parts[1].trim();
        
        if (monthMap[firstPart]) {
          month = monthMap[firstPart];
          year = secondPart.length === 2 ? `20${secondPart}` : secondPart;
        } else if (!isNaN(firstPart)) {
          month = firstPart.padStart(2, '0');
          year = secondPart.length === 2 ? `20${secondPart}` : secondPart;
        }
      }
    }
    
    if (year && month) {
      // Calcular primeiro e último dia do mês
      const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
      const endDate = new Date(parseInt(year), parseInt(month), 0); // Último dia do mês
      
      const formatDate = (date) => {
        return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
      };
      
      const params = new URLSearchParams();
      params.set('page', '1');
      params.set('filterId', '9424055e-1596-4549-9c50-df5ee0da9991');
      params.set('startDate', formatDate(startDate));
      params.set('endDate', formatDate(endDate));
      
      console.log('Navegando para acordos:', {
        month: monthRaw,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate)
      });
      
      navigate(`/app/dados-consolidados?${params.toString()}`);
    } else {
      console.error('Não foi possível parsear a data:', monthRaw);
    }
    
    setShowDataModal(false);
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: handleChartClick,
    plugins: {
      legend: {
        display: true,
        position: "top",
        align: "end",
        labels: {
          boxHeight: 6,
          color: "#858585",
          font: { size: 14 }
        }
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.dataset.label || '';
            const value = context.raw;
            if (context.dataset.yAxisID === 'y1') {
              return `${label}: R$ ${Number(value).toLocaleString('pt-BR')}`;
            }
            return `${label}: ${value}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false }
      },
      y: {
        type: "linear",
        position: "left",
        title: {
          display: true,
          text: "Qtde de Acordos"
        },
        ticks: {
          stepSize: 1
        },
        grid: {
          drawOnChartArea: true
        }
      },
      y1: {
        type: "linear",
        position: "right",
        title: {
          display: true,
          text: "Valor Recebido (R$)"
        },
        ticks: {
          callback: (value) => `R$ ${Number(value).toLocaleString('pt-BR')}`
        },
        grid: {
          drawOnChartArea: false
        }
      }
    }
  };


  return (
    <>
      <FinanceCardChart
        code={code}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>Acordos Realizados</span>
            <div style={{ position: 'relative' }}>
              <img
                src={InfoIcon}
                alt="Informação"
                onClick={() => setShowPopover(!showPopover)}
                style={{
                  width: '14px',
                  height: '14px',
                  cursor: 'pointer',
                  verticalAlign: 'middle',
                  display: 'inline-block'
                }}
              />
              {showPopover && (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "100%",
                    transform: "translate(0, -50%)",
                    marginLeft: "8px",
                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    padding: "10px",
                    zIndex: 100,
                    width: "220px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    borderRadius: "4px"
                  }}
                >
                  <p style={{ margin: 0, fontSize: "12px", fontWeight: "normal" }}>
                    Acordos realizados representa o total de negociações realizadas no mês, contando quantas vezes um acordo de pagamento foi registrado, agrupado por mês de pagamento.
                  </p>
                  <button
                    onClick={() => setShowPopover(false)}
                    style={{
                      marginTop: "8px",
                      fontSize: "10px",
                      cursor: "pointer",
                      background: "none",
                      border: "none",
                      color: "#0962AD"
                    }}
                  >
                    Fechar
                  </button>
                </div>
              )}
            </div>
          </div>
        }
      >
        <Line data={data} options={options} />
      </FinanceCardChart>

      {showDataModal && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999
            }}
            onClick={() => setShowDataModal(false)}
          />
          <div
            style={{
              position: "fixed",
              top: modalPosition.y + 10,
              left: modalPosition.x - 100,
              backgroundColor: "#fff",
              padding: "12px 16px",
              borderRadius: "6px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              border: "1px solid #e0e0e0",
              zIndex: 1000,
              minWidth: "200px",
              maxWidth: "250px"
            }}
          >
            <p style={{ margin: "0 0 12px 0", fontSize: "13px", color: "#333", fontWeight: "500" }}>
              Acordos - {clickedMonthData?.formatted || ''}
            </p>
            <button
              onClick={handleNavigateToData}
              style={{
                width: "100%",
                padding: "6px 12px",
                fontSize: "13px",
                cursor: "pointer",
                backgroundColor: "#0962AD",
                border: "none",
                borderRadius: "4px",
                color: "#fff"
              }}
            >
              Ver dados consolidados
            </button>
          </div>
        </>
      )}
    </>
  );
};

export default FinanceInvoicing;
